// <pdp-slider> — dependency-free scroll-snap slider used by every pdp-* carousel.
//
// Markup:
//   <pdp-slider [data-loop] [data-sync="OtherSliderId"]>
//     <div class="pdp-slider__track" data-pdp-track>
//       <div class="pdp-slider__slide" data-pdp-slide>…</div>
//     </div>
//     <button data-pdp-prev>  <button data-pdp-next>
//     <div data-pdp-dots></div>                (dots generated per snap position)
//     <div data-pdp-bar><span data-pdp-bar-thumb></span></div>
//   </pdp-slider>
// Events: dispatches `pdp:slide-change` {index}; listens for `pdp:goto` {index}.
// Images rendered with data-pdp-srcset (snippets/pdp-image.liquid, defer: true) are loaded
// when they come within ~half a slide of view, or as soon as a slide is jumped to.

class PdpSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-pdp-track]');
    if (!this.track) return;
    this.loop = this.hasAttribute('data-loop');
    this.prevBtn = this.querySelector('[data-pdp-prev]');
    this.nextBtn = this.querySelector('[data-pdp-next]');
    this.dotsEl = this.querySelector('[data-pdp-dots]');
    this.bar = this.querySelector('[data-pdp-bar]');
    this.barThumb = this.querySelector('[data-pdp-bar-thumb]');
    this.current = -1;

    this.onPrev = () => this.step(-1);
    this.onNext = () => this.step(1);
    this.onScroll = () => {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = null;
        this.update();
      });
    };
    this.onGoto = (e) => this.goTo(e.detail.index, e.detail.instant ? 'auto' : 'smooth');
    this.onKey = (e) => {
      if (e.key === 'ArrowLeft') this.step(-1);
      if (e.key === 'ArrowRight') this.step(1);
    };
    this.onBlockSelect = (e) => {
      const slide = e.target.closest && e.target.closest('[data-pdp-slide]');
      if (slide && this.track.contains(slide)) this.goTo(this.slides().indexOf(slide), 'auto');
    };
    this.onSlideClick = (e) => {
      if (!this.syncTarget || this.dragMoved) return;
      const slide = e.target.closest('[data-pdp-slide]');
      if (!slide) return;
      e.preventDefault();
      this.syncTarget.goTo(this.slides().indexOf(slide));
    };

    this.prevBtn && this.prevBtn.addEventListener('click', this.onPrev);
    this.nextBtn && this.nextBtn.addEventListener('click', this.onNext);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.track.addEventListener('keydown', this.onKey);
    this.track.addEventListener('click', this.onSlideClick);
    this.addEventListener('pdp:goto', this.onGoto);
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    this.setupDrag();
    this.setupDeferred();

    this.resizeObserver = new ResizeObserver(() => {
      this.buildDots();
      this.update(true);
    });
    this.resizeObserver.observe(this.track);

    // Thumbnail strip synced to a main slider.
    if (this.dataset.sync) {
      const linkSync = () => {
        this.syncTarget = document.getElementById(this.dataset.sync);
        if (!this.syncTarget) return;
        this.onSyncChange = (e) => this.markActive(e.detail.index);
        this.syncTarget.addEventListener('pdp:slide-change', this.onSyncChange);
        this.markActive(Math.max(0, this.syncTarget.current));
      };
      customElements.whenDefined('pdp-slider').then(linkSync);
    }
    this.buildDots();
    this.update(true);
  }

  disconnectedCallback() {
    if (!this.track) return;
    this.prevBtn && this.prevBtn.removeEventListener('click', this.onPrev);
    this.nextBtn && this.nextBtn.removeEventListener('click', this.onNext);
    this.track.removeEventListener('scroll', this.onScroll);
    this.track.removeEventListener('keydown', this.onKey);
    this.track.removeEventListener('click', this.onSlideClick);
    this.removeEventListener('pdp:goto', this.onGoto);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    this.resizeObserver && this.resizeObserver.disconnect();
    this.deferIO && this.deferIO.disconnect();
    if (this.syncTarget && this.onSyncChange) this.syncTarget.removeEventListener('pdp:slide-change', this.onSyncChange);
    this.teardownDrag && this.teardownDrag();
  }

  slides() {
    return [...this.track.querySelectorAll(':scope > [data-pdp-slide]')].filter((s) => s.offsetParent !== null || s.getClientRects().length);
  }

  maxScroll() {
    return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
  }

  offsetOf(slide) {
    const pad = parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0;
    const rel = slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft - pad;
    return Math.min(Math.max(0, Math.round(rel)), this.maxScroll());
  }

  // Distinct scroll positions the user can land on (what dots represent).
  positions() {
    const seen = [];
    for (const s of this.slides()) {
      const o = this.offsetOf(s);
      if (!seen.some((x) => Math.abs(x - o) < 2)) seen.push(o);
    }
    return seen.length ? seen : [0];
  }

  nearestIndex() {
    const x = this.track.scrollLeft;
    let best = 0;
    let bestD = Infinity;
    this.slides().forEach((s, i) => {
      const d = Math.abs(this.offsetOf(s) - x);
      if (d < bestD - 0.5) {
        bestD = d;
        best = i;
      }
    });
    return best;
  }

  goTo(index, behavior = 'smooth') {
    const slides = this.slides();
    if (!slides.length) return;
    let i = index;
    if (this.loop) i = (i + slides.length) % slides.length;
    i = Math.max(0, Math.min(i, slides.length - 1));
    this.hydrate(slides[i]);
    this.track.scrollTo({ left: this.offsetOf(slides[i]), behavior });
    if (behavior === 'auto') this.update();
  }

  step(dir) {
    const slides = this.slides();
    const at = this.nearestIndex();
    const x = this.track.scrollLeft;
    const max = this.maxScroll();
    if (!this.loop) {
      if (dir > 0 && x >= max - 1) return;
      if (dir < 0 && x <= 1) return;
    } else if (dir > 0 && (at >= slides.length - 1 || x >= max - 1) && slides.length > 1) {
      return this.goTo(0);
    } else if (dir < 0 && at <= 0 && x <= 1) {
      return this.goTo(slides.length - 1);
    }
    // Advance to the next slide whose offset differs from the current position.
    let target = at + dir;
    while (target > 0 && target < slides.length - 1 && Math.abs(this.offsetOf(slides[target]) - x) < 2) target += dir;
    this.goTo(target);
  }

  buildDots() {
    if (!this.dotsEl) return;
    const count = this.positions().length;
    if (this.dotsEl.children.length === count) return;
    this.dotsEl.innerHTML = '';
    const label = this.dotsEl.dataset.label || 'Go to slide';
    for (let i = 0; i < count; i++) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'pdp-slider__dot';
      b.setAttribute('aria-label', `${label} ${i + 1}`);
      b.addEventListener('click', () => {
        const pos = this.positions()[i];
        this.track.scrollTo({ left: pos, behavior: 'smooth' });
      });
      this.dotsEl.appendChild(b);
    }
  }

  update(force) {
    const x = this.track.scrollLeft;
    const max = this.maxScroll();
    const idx = this.nearestIndex();
    if (!this.loop) {
      if (this.prevBtn) this.toggleDisabled(this.prevBtn, x <= 1);
      if (this.nextBtn) this.toggleDisabled(this.nextBtn, x >= max - 1);
    }
    this.classList.toggle('is-scrollable', max > 1);
    if (this.dotsEl) {
      const pos = this.positions();
      let active = 0;
      let bestD = Infinity;
      pos.forEach((p, i) => {
        const d = Math.abs(p - x);
        if (d < bestD) {
          bestD = d;
          active = i;
        }
      });
      [...this.dotsEl.children].forEach((d, i) => {
        d.classList.toggle('is-active', i === active);
        d.setAttribute('aria-current', i === active ? 'true' : 'false');
      });
    }
    if (this.bar && this.barThumb) {
      const ratio = this.track.scrollWidth ? this.track.clientWidth / this.track.scrollWidth : 1;
      const barW = this.bar.clientWidth;
      const thumbW = Math.max(ratio * barW, 8);
      this.barThumb.style.width = `${thumbW}px`;
      this.barThumb.style.transform = `translateX(${max ? (x / max) * (barW - thumbW) : 0}px)`;
    }
    if (idx !== this.current || force) {
      this.current = idx;
      this.slides().forEach((s, i) => s.classList.toggle('is-selected', i === idx));
      this.dispatchEvent(new CustomEvent('pdp:slide-change', { detail: { index: idx } }));
    }
  }

  toggleDisabled(btn, off) {
    btn.toggleAttribute('disabled', off);
    btn.classList.toggle('is-disabled', off);
  }

  markActive(index) {
    const slides = this.slides();
    slides.forEach((s, i) => s.classList.toggle('is-active', i === index));
    const active = slides[index];
    if (!active) return;
    const left = this.offsetOf(active);
    const vis0 = this.track.scrollLeft;
    const vis1 = vis0 + this.track.clientWidth;
    const w = active.getBoundingClientRect().width;
    if (left < vis0 || left + w > vis1) this.track.scrollTo({ left: Math.max(0, left - (this.track.clientWidth - w) / 2), behavior: 'smooth' });
  }

  // Load deferred images inside `root` now.
  hydrate(root) {
    if (!root) return;
    const imgs = root.matches && root.matches('img[data-pdp-srcset]') ? [root] : root.querySelectorAll('img[data-pdp-srcset]');
    for (const img of imgs) {
      img.srcset = img.dataset.pdpSrcset;
      img.src = img.dataset.pdpSrc;
      img.removeAttribute('data-pdp-srcset');
      img.removeAttribute('data-pdp-src');
      this.deferIO && this.deferIO.unobserve(img);
    }
  }

  setupDeferred() {
    const imgs = this.track.querySelectorAll('img[data-pdp-srcset]');
    if (!imgs.length) return;
    if (!('IntersectionObserver' in window)) return this.hydrate(this.track);
    this.deferIO = new IntersectionObserver((entries) => {
      for (const e of entries) if (e.isIntersecting) this.hydrate(e.target);
    }, { root: this.track, rootMargin: '0px 60%' });
    imgs.forEach((img) => this.deferIO.observe(img));
  }

  // Mouse drag (touch uses native scrolling).
  setupDrag() {
    let startX = 0;
    let startLeft = 0;
    let down = false;
    const onDown = (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0 || this.maxScroll() < 2) return;
      down = true;
      this.dragMoved = false;
      startX = e.clientX;
      startLeft = this.track.scrollLeft;
    };
    const onMove = (e) => {
      if (!down) return;
      const dx = e.clientX - startX;
      if (!this.dragMoved && Math.abs(dx) > 5) {
        this.dragMoved = true;
        this.track.classList.add('is-dragging');
      }
      if (this.dragMoved) this.track.scrollLeft = startLeft - dx;
    };
    const onUp = (e) => {
      if (!down) return;
      down = false;
      if (!this.dragMoved) return;
      const dx = e.clientX - startX;
      this.track.classList.remove('is-dragging');
      const slides = this.slides();
      let i = this.nearestIndex();
      const from = slides[i] ? this.offsetOf(slides[i]) : 0;
      if (Math.abs(dx) > 40 && Math.abs(this.track.scrollLeft - from) < 4) i += dx < 0 ? 1 : -1;
      this.goTo(i);
      setTimeout(() => (this.dragMoved = false), 0);
    };
    this.track.addEventListener('pointerdown', onDown);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    this.teardownDrag = () => {
      this.track.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }
}

if (!customElements.get('pdp-slider')) customElements.define('pdp-slider', PdpSlider);
